import { registerSW } from 'virtual:pwa-register'
import { useSyncStore } from '@/store'

/**
 * Registers the Workbox service worker.
 *
 * `registerType: 'prompt'` means a new build never swaps under the user's
 * feet mid-edit — the offline banner offers a reload instead.
 */
export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      useSyncStore.getState().setUpdateReady(true, () => void updateSW(true))
    },
    onOfflineReady() {
      console.info('[pwa] Ready to work offline.')
    },
    onRegisteredSW(swUrl, registration) {
      console.info('[pwa] Service worker registered:', swUrl)
      // Check for a new build every hour while the app stays open.
      if (registration) {
        window.setInterval(() => void registration.update(), 60 * 60 * 1000)
      }
    },
    onRegisterError(error) {
      console.error('[pwa] Service worker registration failed', error)
    },
  })
}

/** Asks the browser to replay the background-sync queue, when supported. */
export async function requestBackgroundSync(tag = 'mymdv-sync-queue'): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  try {
    const registration = await navigator.serviceWorker.ready
    const sync = (registration as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> }
    }).sync
    await sync?.register(tag)
  } catch (error) {
    // Background Sync is Chromium-only; the in-app queue covers everyone else.
    console.debug('[pwa] Background sync unavailable', error)
  }
}
